import React, { useState, useEffect, createContext, useContext } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { ROLES } from './lib/constants'
import { DEFAULT_SETTINGS } from './lib/pricing'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import './styles.css'

// ─── AUTH CONTEXT ─────────────────────────────────────────────────────────────
export const AuthContext = createContext(null)
export const SettingsContext = createContext(DEFAULT_SETTINGS)

export function useAuth() { return useContext(AuthContext) }
export function useSettings() { return useContext(SettingsContext) }

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null)
  const [role, setRole] = useState('viewer')
  const [loading, setLoading] = useState(true)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchRole(session.user.id)
      else setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchRole(session.user.id)
      else { setRole('viewer'); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [])

  async function fetchRole(userId) {
    try {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single()
      setRole(data?.role || 'viewer')
    } catch {
      setRole('viewer')
    } finally {
      setLoading(false)
    }
  }

  async function fetchSettings() {
    try {
      const { data } = await supabase.from('settings').select('key, value')
      if (data) {
        const s = {}
        data.forEach(({ key, value }) => { s[key] = parseFloat(value) || value })
        setSettings(prev => ({ ...prev, ...s }))
      }
    } catch {
      // Use defaults if settings table not yet created
    }
  }

  async function updateSettings(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }))
    try {
      await supabase.from('settings').upsert({ key, value: String(value) })
    } catch (e) {
      console.error('Failed to save setting:', e)
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">YVG PORTAL</div>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const rolePerms = ROLES[role] || ROLES.viewer

  return (
    <AuthContext.Provider value={{ user, role, rolePerms }}>
      <SettingsContext.Provider value={{ settings, updateSettings }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
            <Route path="/*" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
      </SettingsContext.Provider>
    </AuthContext.Provider>
  )
}
